import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, XCircle, FileSignature, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EUR = (v, c = "EUR") => new Intl.NumberFormat("de-DE", { style: "currency", currency: c }).format(v || 0);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("de-DE") : "—");

export default function QuoteAccept() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/public/quotes/${token}`);
        if (!res.ok) throw new Error("not_found");
        setQuote(await res.json());
      } catch (e) {
        setError("Angebot nicht gefunden oder Link ungültig.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const act = async (action, reason = null) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/public/quotes/${token}/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: action === "decline" ? JSON.stringify({ reason: reason || null }) : undefined,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "error");
      setQuote((q) => ({ ...q, status: data.status }));
      setDeclineOpen(false);
    } catch (e) {
      setError(typeof e.message === "string" ? e.message : "Aktion fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error && !quote) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="quote-accept-error">
        <div className="text-center max-w-md">
          <XCircle className="h-12 w-12 mx-auto text-red-400 mb-4" />
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Angebot nicht verfügbar</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  const accepted = quote.status === "accepted" || quote.status === "converted";
  const declined = quote.status === "declined";
  const expired = quote.status === "expired";
  const closed = accepted || declined || expired;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="quote-accept-page">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-slate-900 text-white px-8 py-6 flex items-center gap-3">
            <FileSignature className="h-6 w-6" />
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-300">Angebot</div>
              <div className="text-lg font-semibold">{quote.quote_number}</div>
            </div>
          </div>

          <div className="px-8 py-6 space-y-5">
            <div className="flex justify-between text-sm">
              <div>
                <div className="text-slate-500">Von</div>
                <div className="font-medium text-slate-900">{quote.company_name || "—"}</div>
              </div>
              <div className="text-right">
                <div className="text-slate-500">Gültig bis</div>
                <div className="font-medium text-slate-900">{fmtDate(quote.valid_until)}</div>
              </div>
            </div>

            <div className="border-t border-slate-100 pt-4">
              {(quote.items || []).map((it, i) => (
                <div key={i} className="flex justify-between py-2 text-sm border-b border-slate-50 last:border-0">
                  <span className="text-slate-700">{it.quantity} × {it.description}</span>
                  <span className="text-slate-900">{EUR((it.quantity || 0) * (it.unit_price || 0), quote.currency)}</span>
                </div>
              ))}
            </div>

            <div className="rounded-lg bg-slate-50 p-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">Netto</span><span>{EUR(quote.subtotal, quote.currency)}</span></div>
              {quote.tax_total > 0 && <div className="flex justify-between"><span className="text-slate-500">USt</span><span>{EUR(quote.tax_total, quote.currency)}</span></div>}
              <div className="flex justify-between font-semibold text-base text-slate-900 pt-1"><span>Gesamt</span><span>{EUR(quote.total, quote.currency)}</span></div>
            </div>

            {quote.notes && <p className="text-sm text-slate-600 whitespace-pre-line">{quote.notes}</p>}

            {accepted && (
              <div className="flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-emerald-700" data-testid="quote-accepted-banner">
                <CheckCircle2 className="h-5 w-5" /> Angebot angenommen. Vielen Dank für Ihren Auftrag!
              </div>
            )}
            {declined && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-red-700">
                <XCircle className="h-5 w-5" /> Sie haben dieses Angebot abgelehnt.
              </div>
            )}
            {expired && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-amber-700">
                Dieses Angebot ist abgelaufen. Bitte kontaktieren Sie den Anbieter für ein neues Angebot.
              </div>
            )}

            {!closed && (
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button className="flex-1" data-testid="quote-accept-button" disabled={busy} onClick={() => act("accept")}>
                  <CheckCircle2 className="h-4 w-4 mr-2" /> Angebot annehmen
                </Button>
                <Button variant="outline" className="flex-1" data-testid="quote-decline-button" disabled={busy} onClick={() => { setDeclineReason(""); setDeclineOpen(true); }}>
                  <XCircle className="h-4 w-4 mr-2" /> Ablehnen
                </Button>
              </div>
            )}
          </div>
        </div>
        <p className="text-center text-xs text-slate-400 mt-6">Bereitgestellt über ivoliq</p>
      </div>

      {/* Ablehnen mit optionaler Begründung + Bestätigung */}
      <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
        <DialogContent className="max-w-md" data-testid="quote-public-decline-dialog">
          <DialogHeader><DialogTitle>Angebot ablehnen?</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-slate-700">Möchten Sie das Angebot {quote.quote_number} wirklich ablehnen? Sie können dem Anbieter optional eine kurze Begründung hinterlassen.</p>
            <div>
              <label className="text-xs text-slate-500">Begründung (optional)</label>
              <Textarea data-testid="quote-public-decline-reason" rows={3} value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} placeholder="z.B. anderes Angebot gewählt, Budget zu hoch …" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineOpen(false)} disabled={busy}>Abbrechen</Button>
            <Button className="bg-red-600 hover:bg-red-700" data-testid="quote-public-decline-confirm" disabled={busy} onClick={() => act("decline", declineReason.trim())}>{busy ? "Wird gesendet…" : "Ablehnen bestätigen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
